import { Button, Col, Form, Row, Spin, message } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCookie } from './../../services/cookies';
import { getCurrency, getAreas, getJobPositions } from './../../services/general';
import countrySettings from './countrySettings.json';
import GeneralData from './components/GeneralData';
import PatientsAndServices from './components/PatientsAndServices';
import ServicesSummary from './components/ServicesSummary';
import ValidationButtons from './components/ValidationButtons';

const ReservationCreatePage = () => {
  const [reservation, setReservation] = useState({ 'state': 'drafted' });
  const [patients, setPatients] = useState([]);
  const [branchOfficeId, setBranchOfficeId] = useState(getCookie('branch_office_id'));
  const [currency, setCurrency] = useState();
  const [refreshForm, setRefreshForm] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [totalPatients, setTotalPatients] = useState();
  const [disablePatientInput, setDisablePatientInput] = useState(false);
  const [summaryItems, setSummaryItems] = useState();
  const [summaryTotal, setSummaryTotal] = useState();
  const [loadingCurrency, setLoadingCurrency] = useState(false);
  const [projects, setProjects] = useState([]);
  const [areasList, setAreasList] = useState([]);
  const [jobPositionsList, setJobPositionsList] = useState([]);
  const [disabled, setDisabled] = useState({ patientBlocking: false })
  const countryCode = getCookie('country_code')
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname
  const { t } = useTranslation();
  const companyId = Form.useWatch('company_id', form);

  const confirmRefreshPage = event => {
    event.preventDefault();

    const message = '¿Estás seguro que deseas abandonar?';

    return event.returnValue = message;
  };

  const groupByService = useCallback(() => {
    const prestationObj = {};
    let totalValue = 0;

    patients.forEach(item => {
      item.prestations.forEach(prestation => {
        const prestationAmount = prestation.sale_amount ?? 0;
        if (!prestationObj[prestation.name]) {
          prestationObj[prestation.name] = {
            id: `${prestation.id}-${prestation.type}`,
            unit_price: prestationAmount,
            sale_amount: 0,
            quantity: 0,
          };
        }

        const oldPrestation = prestationObj[prestation.name];

        prestationObj[prestation.name] = {
          ...oldPrestation,
          sale_amount: parseFloat(oldPrestation.sale_amount) + parseFloat(prestationAmount),
          quantity: oldPrestation.quantity + 1,
        };

        totalValue += parseFloat(prestationAmount);
      });
    });


    const prestationArray = Object.keys(prestationObj).map(prestationName => {
      return {
        ...prestationObj[prestationName],
        name: prestationName
      }
    });

    const locateCode = currency ? currency.locateCode : 'es-CL';
    const currencyCode = currency ? currency.currencyCode : 'CLP';

    setSummaryItems(prestationArray);
    setSummaryTotal({ totalValue, locateCode, currencyCode });

  }, [patients, currency]);

  useEffect(() => {
    if (patients.length > 0 && totalPatients) {
      checkAdmittedPatients();
    }

    if (!branchOfficeId) {
      setBranchOfficeId(getCookie('branch_office_id'))
    }

    if (patients) {
      groupByService();
    }
    window.addEventListener('beforeunload', confirmRefreshPage);

    return () => {
      window.removeEventListener('beforeunload', confirmRefreshPage);
    }

  }, [groupByService, patients, totalPatients, branchOfficeId]);

  const fetchCurrency = useCallback(async () => {
    try {
      setLoadingCurrency(true)
      const response = await getCurrency();
      setCurrency({ locateCode: response.locate_code, currencyCode: response.code });
    } catch (error) {
      console.log(error);
      message.error({
        content: `Error interno ${error}`,
        style: {
          marginTop: '32vh',
          fontSize: 16,
        },
      });
    } finally {
      setLoadingCurrency(false);
    }
  }, []);

  useEffect(() => {
    if (companyId) {
      getAreas({ limit: 100, company_id: companyId })
        .then(res => setAreasList(res || []))
        .catch(error => {
          console.error("Error fetching areas:", error);
          message.error("Error al obtener áreas");
        });
      getJobPositions({ limit: 100, company_id: companyId })
        .then(res => setJobPositionsList(res || []))
        .catch(error => {
          console.error("Error fetching job positions:", error);
          message.error("Error al obtener puestos de trabajo");
        });
    } else {
      setAreasList([]);
      setJobPositionsList([]);
    }
  }, [companyId]);

  const handleAreaCreated = useCallback((newArea) => {
    setAreasList(prev => [...prev, newArea]);
  }, []);

  const handleJobPositionCreated = useCallback((newJobPosition) => {
    setJobPositionsList(prev => [...prev, newJobPosition]);
  }, []);

  const checkAdmittedPatients = () => {
    let _patients = patients;
    form.setFieldsValue({ 'total_patients': patients.length });

    const patientsWithoutServices = _patients.filter((item) => item['prestations'].length === 0);
    const patientsWithBlocks = _patients.filter((item) => item['blocks']);

    if (patientsWithoutServices.length === totalPatients || patientsWithBlocks === 0) {
      setDisablePatientInput(false);
    } else {
      setDisablePatientInput(true);
    }
  };

  const handleBack = () => {
    navigate(-1)
  };

  return (
    <Row justify="center" className="reservation-create-wrapper mb-80">
      <Col lg={24} xs={24}>
        <Row justify="center" className="reservation-create-wrapper__header pt-15 pb-20">
          <Col lg={22} xs={22}>
            <Row>
              <Col className="back-button-wrapper">
                <Button onClick={handleBack}>
                  <i className="icon-system-chevron-left" />
                  Volver
                </Button>
              </Col>
              <Col lg={24} xs={24}>
                <h1 className="text-center mb-0">
                  {t('reservations.newReservation.title')}
                </h1>
              </Col>
            </Row>
          </Col>
        </Row>
        {
          reservation &&
          <GeneralData
            type={'creation'}
            form={form}
            reservation={reservation}
            setReservation={setReservation}
            setTotalPatients={setTotalPatients}
            disablePatientInput={disablePatientInput}
            setRefreshForm={setRefreshForm}
            setBranchOfficeId={setBranchOfficeId}
            sessionBranchOfficeId={branchOfficeId}
            fetchCurrency={fetchCurrency}
            companies={companies}
            setCompanies={setCompanies}
            countrySettings={countrySettings}
            countryCode={countryCode}
          />
        }

        {
          reservation && (totalPatients > 0
            && form.getFieldValue('branch_office_id')
            && form.getFieldValue('reservation_date')) &&
          <Fragment>
            <PatientsAndServices
              form={form}
              type={'creation'}
              patients={patients}
              setPatients={setPatients}
              totalPatients={totalPatients}
              setTotalPatients={setTotalPatients}
              countryCode={countryCode}
              currentPage={currentPage}
              countrySettings={countrySettings}
              setDisabled={setDisabled}
              projects={projects}
              setProjects={setProjects}
              areasList={areasList}
              jobPositionsList={jobPositionsList}
              handleAreaCreated={handleAreaCreated}
              handleJobPositionCreated={handleJobPositionCreated}
            />
            {countrySettings.showServicesSummary[countryCode] && (
              <Col offset={1} lg={22} xs={22}>
                <Spin className='cmt-spin' spinning={loadingCurrency}>
                  <ServicesSummary
                    summaryItems={summaryItems}
                    summaryTotal={summaryTotal}
                    currency={currency}
                  />
                </Spin>
              </Col>
            )}
            {form &&
              <ValidationButtons
                type={'creation'}
                form={form}
                patients={patients}
                branchOfficeId={branchOfficeId}
                companies={companies}
                countryCode={countryCode}
                disabled={disabled}
              />
            }
          </Fragment>
        }
      </Col>
    </Row>
  );
};

export default ReservationCreatePage;
